import {
  CplAdditionalCashRange,
  CplAverageMonthlyExpensesRange,
  CplAverageMonthlyIncomeRange,
  CplCreditProfileDescription,
  CplEmploymentStatus,
  CplEstimatedValueRange,
  CplFirstMortgageRate,
  CplHasLatePayments,
  CplHomePurchaseYearRange,
  CplLoanType,
  CplMortgageBalanceRange,
  CplPropertyType,
  CplPropertyUse,
  CplRateType,
  CplYesOrNo,
  LoanPurpose,
  PropertyType,
  PropertyUse,
  RefinanceMortgageFormValues,
} from '@mortgage-pos/types'

export const translateAnswersToCPLFormat = ({
  mergedAnswers,
  currentMortgageDetails,
}): RefinanceMortgageFormValues => {
  return {
    fullName: {
      firstName: mergedAnswers.firstName || null,
      lastName: mergedAnswers.lastName || null,
    },
    zipCode: mergedAnswers.zipCode,
    loanType:
      mapCplLoanType(mergedAnswers.loanPurpose) || CplLoanType.Refinance,
    housePurchaseYear:
      mapCplHomePurchaseYear(mergedAnswers.currentLoanStartDate) ||
      CplHomePurchaseYearRange.TwentyTenToPresent,
    propertyType:
      mapCplPropertyType(mergedAnswers.propertyType) ||
      CplPropertyType.SingleFamily,
    propertyEstimatedValueRange:
      mapAlfieCurrencyToRange(
        mergedAnswers.estimatedValue,
        CplEstimatedValueRange
      ) || CplEstimatedValueRange.TwoForty,
    firstMortgageBalanceRange:
      mapAlfieCurrencyToRange(
        currentMortgageDetails.currentBalance,
        CplMortgageBalanceRange
      ) || CplMortgageBalanceRange.TwoHundred,
    firstMortgageRate:
      mapAlfieRateToCplRange(
        parseFloat(currentMortgageDetails.estimatedInterestRate)
      ) || CplFirstMortgageRate.Five,
    existingRateType: CplRateType.Fixed, // Defaulted
    hasSecondMortgage: CplYesOrNo.Yes, // Defaulted
    creditProfileDescription: CplCreditProfileDescription.OverSevenFourty, // Defaulted
    propertyUse:
      mapCplPropertyUse(mergedAnswers.propertyUse) || CplPropertyUse.Primary,
    additionalCashRange:
      mapAlfieCurrencyToRange(mergedAnswers.cashOut, CplAdditionalCashRange) ||
      CplAdditionalCashRange.NoCash,
    hasFHALoan: CplYesOrNo.No, // Defaulted
    hasFiledBankruptcy: CplYesOrNo.No, // Defaulted
    hasLatePayments: CplHasLatePayments.No, // Defaulted
    employmentStatus: CplEmploymentStatus.Employed, // Defaulted
    averageMonthlyIncomeRange: CplAverageMonthlyIncomeRange.FiveThousand, // Defaulted
    averageMonthlyExpensesRange: CplAverageMonthlyExpensesRange.ThreeThousand, // Defaulted
    hasProofIncome: CplYesOrNo.Yes, // Defaulted
  }
}

export const mapCplLoanType = (loanPurpose) => {
  return loanPurpose === LoanPurpose.Refinance
    ? CplLoanType.Refinance
    : CplLoanType.Purchase
}

export const mapCplHomePurchaseYear = (
  loanStartDate
): CplHomePurchaseYearRange => {
  const year = new Date(loanStartDate).getFullYear()

  switch (true) {
    case year < 1990:
      return CplHomePurchaseYearRange.BeforeNinety
    case year >= 1990 && year <= 1999:
      return CplHomePurchaseYearRange.NinetyToNinetyNine
    case year >= 2000 && year <= 2009:
      return CplHomePurchaseYearRange.TwoThousandToTwoThousandNine
    case year >= 2010:
      return CplHomePurchaseYearRange.TwentyTenToPresent
    default:
      return null
  }
}

export const mapCplPropertyType = (propertyType): CplPropertyType => {
  switch (propertyType) {
    case PropertyType.Townhouse:
      return CplPropertyType.TownHome

    case PropertyType.Condo4OrFewerStories:
    case PropertyType.Condo5OrMoreStories:
      return CplPropertyType.Condominium

    case PropertyType.MultiFamily2Units:
    case PropertyType.MultiFamily3Units:
    case PropertyType.MultiFamily4Units:
      return CplPropertyType.MultiFamily

    // All other classified as single Family
    default:
      return CplPropertyType.SingleFamily
  }
}

export const mapCplPropertyUse = (propertyUse): CplPropertyUse => {
  switch (propertyUse) {
    case PropertyUse.SecondaryOrVacation:
      return CplPropertyUse.Secondary
    case PropertyUse.InvestmentOrRental:
      return CplPropertyUse.Rental
    case PropertyUse.PrimaryResidence:
    default:
      return CplPropertyUse.Primary // Default
  }
}

export const mapCplCreditProfile = (
  creditScore
): CplCreditProfileDescription => {
  switch (true) {
    case creditScore >= 740:
      return CplCreditProfileDescription.OverSevenFourty
    case creditScore <= 739 && creditScore >= 720:
      return CplCreditProfileDescription.SevenThirtyNine
    case creditScore <= 719 && creditScore >= 700:
      return CplCreditProfileDescription.SevenNineteen
    case creditScore <= 699 && creditScore >= 680:
      return CplCreditProfileDescription.SixNinetyNine
    case creditScore <= 679 && creditScore >= 660:
      return CplCreditProfileDescription.SixSeventyNine
    case creditScore <= 659 && creditScore >= 640:
      return CplCreditProfileDescription.SixFiftyNine
    case creditScore <= 639 && creditScore >= 620:
      return CplCreditProfileDescription.SixThirtyNine
    case creditScore <= 619 && creditScore >= 580:
    case creditScore < 580:
      return CplCreditProfileDescription.SixNineteen
    default:
      return null
  }
}

/**
 * @name numberRangeToArray()
 * @desc It will take a string and locate all of its numbers and return them converted to numbers in an array
 * // numberRangeToArray('$20,000 to $25,000') -> [20000, 25000]
 * // numberRangeToArray('2.88%') -> [2.88]
 */
export const numberRangeToArray = (string: string): number[] => {
  if (!string) {
    return []
  }

  return string
    .replace(/,/g, '')
    .match(/[\d.]+/g)
    .map(Number)
}

export const roundNumberToQuarterPercent = (number) =>
  Math.round(number * 4) / 4

export const mapAlfieRateToCplRange = (rate): CplFirstMortgageRate => {
  // round rate to 1/4 percentages
  const roundedRate = roundNumberToQuarterPercent(rate)

  //if rate is under 2 or 12 and over return that enum
  if (roundedRate < 2) {
    return CplFirstMortgageRate.UnderTwo
  }
  if (roundedRate >= 12) {
    return CplFirstMortgageRate.OverTwelve
  }

  const rateTuple = Object.entries(CplFirstMortgageRate)
    .map((entry: [string, string]): [string, number] => [
      entry[0],
      numberRangeToArray(entry[1])[0],
    ])
    .filter((entry) => {
      const excludes = ['UnderTwo', 'OverTwelve']
      return !excludes.includes(entry[0])
    })

  const firstMortgageRate = rateTuple.find((item) => {
    return item[1] === roundedRate
  })

  if (!firstMortgageRate) {
    return CplFirstMortgageRate.Five // Default Value
  }

  return CplFirstMortgageRate[firstMortgageRate[0]]
}

export const mapAlfieCurrencyToRange = (amount, enumObject) => {
  if (amount !== 0 && !amount) {
    return null
  }

  const currencyRanges = Object.entries(enumObject).map(
    ([enumKey, enumValue]: [string, string]): [string, number[]] => [
      enumKey,
      numberRangeToArray(enumValue), // Convert range String to number only array equivalent
    ]
  )

  const [firstEnumKey, firstEnumValue] = [...currencyRanges].shift()
  const [lastEnumKey, lastEnumValue] = [...currencyRanges].pop()

  // If the firstEnum is not a range (length === 1)
  // AND that enum and amount are both 0 OR amount is lower than enum
  // ie. [ 0 ] is the "range" and amount is exactly 0
  // ie. [ 80000 ] is the "range" and amount is less than or equal to 80000
  if (
    firstEnumValue.length === 1 &&
    ((firstEnumValue[0] === 0 && amount === 0) || amount <= firstEnumValue[0])
  ) {
    return enumObject[firstEnumKey]
  }

  // If the lastEnum is not a range (length === 1) and amount is greater
  if (lastEnumValue.length === 1 && amount > lastEnumValue) {
    return enumObject[lastEnumKey]
  }

  // Else all other Enums are Ranges
  const [matchedRangeKey] = currencyRanges.find(
    ([_, [lowLimit, highLimit]]) => {
      const hasLowAndHigh = (!!lowLimit || lowLimit === 0) && !!highLimit

      return hasLowAndHigh && amount >= lowLimit && amount <= highLimit
    }
  )

  return enumObject[matchedRangeKey]
}
